import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Check, CreditCard, Shield, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const [selectedPackage, setSelectedPackage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("plan") || "starter";
  });
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/create-session`, {
        package_id: selectedPackage,
        origin_url: originUrl
      });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Unable to process checkout. Please try again.");
      setLoading(false);
    }
  };

  const selectedPackageData = packages.find(p => p.id === selectedPackage);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      <section className="pt-32 pb-24 px-6 lg:px-12" data-testid="checkout-section">
        <div className="container mx-auto max-w-5xl">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-4">
              Secure Checkout
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-4">
              Start Your Recovery Journey
            </h1>
            <p className="text-base text-[#1A2A3A]/60 max-w-2xl mx-auto">
              Choose your plan and begin healing today. Every plan includes our free tools. Cancel anytime. We do not promise to cure any disease or mental illness.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Package Selection */}
            <div>
              <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6">
                Select Your Plan
              </h2>
              
              <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage} className="space-y-4">
                {packages.map((pkg) => (
                  <div key={pkg.id}>
                    <Label
                      htmlFor={pkg.id}
                      className={`flex cursor-pointer rounded-2xl border-2 p-6 transition-all ${
                        selectedPackage === pkg.id
                          ? "border-[#4FB3D4] bg-[#4FB3D4]/5"
                          : "border-[#E5E7EB] hover:border-[#4FB3D4]/50"
                      }`}
                      data-testid={`package-${pkg.id}`}
                    >
                      <RadioGroupItem value={pkg.id} id={pkg.id} className="sr-only" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-[#1A2A3A]">{pkg.name}</span>
                          <span className="font-heading text-2xl font-bold text-[#4FB3D4]">
                            ${pkg.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-[#1A2A3A]/60 mb-4">{pkg.description}</p>
                        <ul className="space-y-2">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-[#1A2A3A]/70">
                              <Check className="h-4 w-4 text-[#4FB3D4]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {selectedPackage === pkg.id && (
                        <div className="ml-4">
                          <div className="w-6 h-6 rounded-full bg-[#4FB3D4] flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {/* What's Included */}
              <div className="mt-8 p-6 bg-gradient-to-br from-[#DCD6F7]/20 to-[#4FB3D4]/10 rounded-2xl">
                <h3 className="font-semibold text-[#1A2A3A] mb-4">What's Included:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#4FB3D4] mt-0.5" />
                    <span className="text-[#1A2A3A]/70">Daily affirmation calendar with powerful healing messages</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#4FB3D4] mt-0.5" />
                    <span className="text-[#1A2A3A]/70">Energy-boosting recipes for physical wellness</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#4FB3D4] mt-0.5" />
                    <span className="text-[#1A2A3A]/70">Comprehensive recovery plan dashboard</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#4FB3D4] mt-0.5" />
                    <span className="text-[#1A2A3A]/70">BONUS: "15 Ways to Stop a Narcissist" PDF guide</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Payment Card */}
            <div>
              <Card className="stripe-card-container shadow-floating border-0" data-testid="payment-card">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 font-heading text-xl text-[#1A2A3A]">
                    <CreditCard className="h-6 w-6 text-[#4FB3D4]" />
                    Secure Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Order Summary */}
                  <div className="bg-[#FDFBF7] rounded-xl p-4">
                    <h4 className="font-medium text-[#1A2A3A] mb-3">Order Summary</h4>
                    <div className="flex justify-between items-center py-2 border-b border-[#E5E7EB]">
                      <span className="text-[#1A2A3A]/70">
                        {selectedPackageData?.name || "Recovery Plan"}
                      </span>
                      <span className="font-semibold text-[#1A2A3A]">
                        ${selectedPackageData?.price.toFixed(2) || "0.00"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3">
                      <span className="font-semibold text-[#1A2A3A]">Total</span>
                      <span className="font-heading text-2xl font-bold text-[#4FB3D4]">
                        ${selectedPackageData?.price.toFixed(2) || "0.00"}
                      </span>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full btn-primary text-lg py-6 rounded-full font-semibold"
                    data-testid="checkout-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-5 w-5" />
                        Proceed to Payment
                      </>
                    )}
                  </Button>

                  {/* Security Badge */}
                  <div className="flex items-center justify-center gap-2 text-sm text-[#1A2A3A]/50">
                    <Shield className="h-4 w-4" />
                    <span>Secured by Stripe • 256-bit SSL encryption</span>
                  </div>

                  {/* Guarantee */}
                  <div className="text-center p-4 bg-[#4FB3D4]/5 rounded-xl">
                    <p className="text-sm text-[#1A2A3A]/70">
                      <strong className="text-[#1A2A3A]">30-Day Money-Back Guarantee</strong>
                      <br />
                      If you're not satisfied, get a full refund within 30 days.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Trust Indicators */}
              <div className="mt-6 text-center">
                <p className="text-sm text-[#1A2A3A]/50">
                  Cancel anytime • No hidden fees • Instant access
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CheckoutPage;
